import React, { useEffect, useId, useRef, useState } from 'react';
import { listVendorInvoices, type VendorInvoice } from '../../lib/vendorInvoiceApi';
import { useLatestRequest } from '../../hooks/useLatestRequest';
import { formatDate } from '../../utils/dateFormat';

/**
 * Pick the vendor invoice a new vendor return is written against (D44).
 *
 * Same shape as components/InvoiceSearchSelect, for vendor invoices: opens on
 * focus with the most recent, filters as you type (debounced), and says when it
 * is searching, found nothing, or could not load. Whether an invoice may take a
 * return at all (it must have been through Confirm & sync) is the server's call,
 * made when the page asks for its lines, so every invoice is offered here.
 */

const RESULT_LIMIT = 10;
const LOAD_FAILED = 'Vendor invoices could not be loaded. Check your connection and try again.';

function invoiceNumberOf(inv: VendorInvoice): string {
  return String(inv.extracted?.invoice?.invoiceNumber || inv.source?.fileName || '(no invoice number)');
}

function vendorNameOf(inv: VendorInvoice): string {
  return String(inv.vendorReconciliation?.matchedVendorName || inv.extracted?.vendor?.name || 'Unknown vendor');
}

export const VendorInvoicePicker: React.FC<{
  onPick: (vendorInvoiceId: string) => void;
  disabled?: boolean;
}> = ({ onPick, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VendorInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputId = useId();
  const listboxId = useId();
  const beginRequest = useLatestRequest();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const isCurrent = beginRequest();
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await listVendorInvoices({ q: query.trim(), limit: RESULT_LIMIT });
        if (cancelled || !isCurrent()) return;
        setResults(res.items || []);
        setLoadError(false);
        setHighlight(0);
      } catch {
        if (cancelled || !isCurrent()) return;
        setResults([]);
        setLoadError(true);
      } finally {
        if (!cancelled && isCurrent()) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open, beginRequest]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (inv: VendorInvoice) => {
    onPick(inv._id);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const inv = results[highlight];
      if (inv) choose(inv);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <label htmlFor={inputId} className="block text-xs font-medium text-slate-600 mb-1">
        Vendor invoice <span className="text-red-600">*</span>
      </label>
      <input
        id={inputId}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search by invoice number or vendor…"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listboxId}
        className="w-full h-9 rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-primary-500 disabled:bg-slate-50"
      />

      {open && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Vendor invoices"
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>}
          {!loading && loadError && <div className="px-3 py-3 text-xs text-red-700">{LOAD_FAILED}</div>}
          {!loading && !loadError && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {query.trim() ? `No vendor invoice matches “${query.trim()}”.` : 'No vendor invoices yet.'}
            </div>
          )}
          {!loading &&
            results.map((inv, i) => (
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                key={inv._id}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(inv)}
                className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 ${
                  i === highlight ? 'bg-slate-50' : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-slate-800">{invoiceNumberOf(inv)}</span>
                  <span className="text-slate-500">
                    {formatDate(inv.extracted?.invoice?.invoiceDate || inv.extracted?.invoice?.date, '--')}
                  </span>
                </div>
                <div className="text-slate-500 truncate">{vendorNameOf(inv)}</div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default VendorInvoicePicker;
